import {ClientLatencyTools} from '@packages/systems/designer/performance-tools';
import {
  TelemetryEventLabel,
  telemetry,
} from '@packages/systems/designer-telemetry';

const LABEL = TelemetryEventLabel.VARIABLE_VALUE_CHANGE_TO_RENDER_LATENCY;

// A commit that never re-renders the canvas would otherwise stay open until
// an unrelated canvas update closes it with an arbitrarily long sample.
export const STALE_MEASUREMENT_MS = 5000;

/**
 * Why a sample was dropped instead of reported. Kept small to bound metric
 * cardinality.
 */
export type VariableChangeDropReason = 'stale' | 'hidden';

/** Where the user committed the new value from. Kept small to bound metric cardinality. */
export type VariableChangeSurface = 'variables-panel' | 'style-panel';

let staleTimer: ReturnType<typeof setTimeout> | undefined;
let startedAt: number | undefined;
let openSurface: VariableChangeSurface | undefined;
let wasHidden = false;

const isHidden = () => typeof document !== 'undefined' && document.hidden;

const hasFocus = () => typeof document === 'undefined' || document.hasFocus();

// Browsers throttle timers and stop animation frames in hidden tabs, so a
// sample that spans a tab switch measures the throttle rather than the
// variable change. See `performance-tools/tabFocusTools.ts` for the same
// rationale applied to page load.
const onVisibilityChange = () => {
  if (isHidden()) wasHidden = true;
};

const watchVisibility = () => {
  if (typeof document === 'undefined') return;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  wasHidden = isHidden();
  document.addEventListener('visibilitychange', onVisibilityChange);
};

const stopWatchingVisibility = () => {
  if (typeof document === 'undefined') return;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  wasHidden = false;
};

const clearMeasurement = () => {
  clearTimeout(staleTimer);
  startedAt = undefined;
  openSurface = undefined;
  stopWatchingVisibility();
};

/**
 * Drops the open measurement and reports why, so a regression that inflates the
 * dropped count stays visible even though no latency sample is emitted.
 */
const dropMeasurement = (
  reason: VariableChangeDropReason,
  surface: VariableChangeSurface | undefined,
  ageMs: number
): void => {
  ClientLatencyTools.cancel(LABEL);
  telemetry.sendEvent(
    `datadog::${TelemetryEventLabel.VARIABLE_VALUE_CHANGE_DROPPED}`,
    {surface, reason, ageMs: Math.round(ageMs)}
  );
};

export const startVariableChangeMeasurement = (
  surface: VariableChangeSurface
): void => {
  // A second `start` without a `cancel` throws internally and drops the whole
  // measurement, so a rapid re-edit would otherwise report nothing.
  ClientLatencyTools.cancel(LABEL);
  ClientLatencyTools.start(LABEL, {surface});
  startedAt = performance.now();
  openSurface = surface;
  watchVisibility();
  clearTimeout(staleTimer);
  staleTimer = setTimeout(() => {
    if (startedAt === undefined) return;
    const age = performance.now() - startedAt;
    const droppedSurface = openSurface;
    const reason = wasHidden ? 'hidden' : 'stale';
    clearMeasurement();
    dropMeasurement(reason, droppedSurface, age);
  }, STALE_MEASUREMENT_MS);
};

export const finishVariableChangeMeasurement = (): void => {
  if (startedAt === undefined) {
    // Nothing open: a previous finish or the stale timer already closed it.
    ClientLatencyTools.cancel(LABEL);
    return;
  }

  // Throttled background tabs (Safari in particular) can postpone the
  // stale-cancel timer above indefinitely, so a measurement can outlive its
  // window despite it. Age the sample out here too, on elapsed clock time.
  const age = performance.now() - startedAt;
  const droppedSurface = openSurface;
  const hidden = wasHidden || isHidden();
  clearMeasurement();

  if (hidden) {
    dropMeasurement('hidden', droppedSurface, age);
    return;
  }
  if (age >= STALE_MEASUREMENT_MS) {
    dropMeasurement('stale', droppedSurface, age);
    return;
  }
  // Whether the page had OS focus at finish is the one thing the SLI cannot
  // infer for itself, and it is the difference between a slow canvas update
  // and a window the user was not looking at.
  ClientLatencyTools.finishAndReport(LABEL, {
    pageFocused: hasFocus(),
  });
};
